import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { useTheme } from "../theme/ThemeContext";
import { useRef, useState, useEffect } from "react";
import { theme } from "@/theme/theme";

function ExperienceCard({ exp, isDark, index }) {
  const experienceStyles = theme.experience[isDark ? 'dark' : 'light'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      className="relative group"
    >
      {/* Main Material Container */}
      <motion.div
        className={`
          relative overflow-hidden rounded-3xl backdrop-blur-sm
          transition-all duration-300 ease-out
          ${isDark ? 'bg-black' : 'bg-white'}
        `}
        style={{
          minHeight: '340px',
          background: isDark 
            ? 'linear-gradient(145deg, #000000 0%, #0a0a0a 100%)'
            : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: isDark
            ? '0 4px 16px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.2)'
            : '0 4px 16px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)'
        }}
        whileHover={{
          scale: 1.02,
          y: -8,
      boxShadow: isDark
            ? '0 8px 32px rgba(0,0,0,0.6), 0 16px 64px rgba(0,0,0,0.3)'
            : '0 8px 32px rgba(0,0,0,0.15), 0 16px 64px rgba(0,0,0,0.1)',
          transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
        }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Material Surface Tint */}
        <motion.div
          className="absolute inset-0 rounded-3xl"
          style={{
            background: isDark
              ? 'linear-gradient(145deg, rgba(249,115,22,0.05) 0%, rgba(20,184,166,0.05) 100%)'
              : 'linear-gradient(145deg, rgba(20,184,166,0.03) 0%, rgba(249,115,22,0.03) 100%)',
          }}
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />

        {/* State Layer for Interactions */}
        <motion.div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(249,115,22,0.1) 0%, transparent 50%)'
              : 'radial-gradient(circle at var(--x, 50%) var(--y, 50%), rgba(20,184,166,0.08) 0%, transparent 50%)',
          }}
          initial={{ opacity: 0, scale: 0 }}
          whileHover={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />

        {/* Content Container */}
        <div className="relative z-10 p-6 h-full flex flex-col justify-between min-h-[320px]">
          {/* Header Section */}
          <div className="flex flex-col">
            {/* Material Chip for Period */}
        <motion.div
              className="mb-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <div className={`
                px-3 py-1.5 rounded-full text-xs font-medium font-mono
                transition-all duration-300 inline-block w-fit
                ${isDark 
                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' 
                  : 'bg-teal-500/15 text-teal-700 border border-teal-500/20'
                }
              `}
              style={{
                boxShadow: isDark
                  ? '0 2px 8px rgba(249,115,22,0.2)'
                  : '0 2px 8px rgba(20,184,166,0.15)'
              }}
              >
                {exp.period}
              </div>
            </motion.div>

            {/* Material Typography - Title */}
            <motion.h3 
              className={`
                text-xl font-bold mb-2 leading-tight font-mono
                ${isDark ? 'text-white' : 'text-gray-900'}
                transition-colors duration-300
              `}
              whileHover={{
                scale: 1.01,
                transition: { duration: 0.2 }
              }}
            >
              {exp.title}
            </motion.h3>

            {/* Material Typography - Subtitle */}
            <motion.p 
              className={`
                text-base font-medium mb-4 font-mono
                ${isDark ? 'text-orange-400' : 'text-teal-600'}
                transition-colors duration-300
              `}
              whileHover={{
                x: 2,
                transition: { duration: 0.2 }
              }}
            >
              {exp.company}
            </motion.p>
            </div>

          {/* Body Section */}
          <div className="flex-1 flex flex-col justify-center">
            {/* Material Typography - Description */}
            <motion.p 
              className={`
                text-sm leading-relaxed font-mono text-left
                ${isDark ? 'text-gray-300' : 'text-gray-700'}
                transition-colors duration-300
              `}
              initial={{ opacity: 0.9 }}
              whileHover={{ 
                opacity: 1,
                transition: { duration: 0.2 }
              }}
            >
              {exp.description}
            </motion.p>
          </div>

          {/* Footer Section */}
          <div className="flex flex-col gap-4 mt-4">
            {/* Material Progress Indicator */}
            <motion.div 
              className="w-full"
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.8 }}
            >
              <div className={`h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
                <motion.div
                  className={`h-full rounded-full ${isDark ? 'bg-orange-500' : 'bg-teal-500'}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${85 + Math.random() * 15}%` }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 1.2, ease: "easeOut" }}
                />
              </div>
            </motion.div>

            {/* Material Status Badge */}
            <motion.div 
              className="flex items-center justify-between w-full"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
            >
              <div className="flex items-center gap-2">
                <motion.div
                  className={`w-2.5 h-2.5 rounded-full ${
                    index === 0 
                      ? (isDark ? 'bg-green-400' : 'bg-orange-500')
                      : (isDark ? 'bg-orange-400' : 'bg-teal-400')
                  }`}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.2
                  }}
                  style={{
                    boxShadow: `0 0 8px ${
                      index === 0 
                        ? (isDark ? 'rgba(74, 222, 128, 0.4)' : 'rgba(249,115,22,0.4)')
                        : (isDark ? 'rgba(249,115,22,0.4)' : 'rgba(20,184,166,0.4)')
                    }`
                  }}
                />
                <span className={`text-xs font-medium font-mono ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {index === 0 ? 'Active' : index === 1 ? 'Recent' : 'Experience'}
              </span>
              </div>
              
              {/* Right aligned status indicator */}
              <div className="flex items-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i <= index ? (isDark ? 'bg-orange-400' : 'bg-teal-400') : (isDark ? 'bg-gray-700' : 'bg-gray-300')
                    }`}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.1, duration: 0.3 }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>


      </motion.div>
    </motion.div>
  );
}

// Floating Particles Component
function FloatingParticle({ delay, isDark }) {
  return (
    <motion.div
      className={`absolute w-1 h-1 rounded-full ${isDark ? 'bg-orange-400' : 'bg-teal-400'}`}
      initial={{ 
        x: Math.random() * 800,
        y: Math.random() * 200,
        opacity: 0,
        scale: 0
      }}
      animate={{
        x: Math.random() * 800,
        y: Math.random() * 200,
        opacity: [0, 0.8, 0],
        scale: [0, 1, 0]
      }}
      transition={{
        duration: 8,
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut"
      }}
      style={{ filter: 'blur(0.5px)' }}
    />
  );
}

// Cyber Status Indicator
function CyberStatusIndicator({ isDark }) {
  return (
    <motion.div 
      className="flex items-center gap-1.5 sm:gap-2 absolute top-4 right-4 z-20"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.2, duration: 0.6 }}
    >
      {/* Pulsing Status Dot */}
      <motion.div
        className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${isDark ? 'bg-green-400' : 'bg-green-500'}`}
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.7, 1, 0.7]
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{ 
          boxShadow: isDark 
            ? '0 0 10px rgba(74, 222, 128, 0.6)' 
            : '0 0 10px rgba(34, 197, 94, 0.6)'
        }}
      />
      
      {/* Status Text */}
      <motion.span 
        className={`text-[10px] sm:text-xs font-mono ${isDark ? 'text-green-400' : 'text-green-600'} tracking-wider`}
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        AVAILABLE
      </motion.span>
    </motion.div>
  );
}

const Experience = () => {
  const { theme: themeMode } = useTheme();
  const isDark = themeMode === 'dark';
  const experienceStyles = theme.experience[themeMode];
  
  const [currentText, setCurrentText] = useState(0);
  const texts = ["Experience", "Open for Freelance"];
  
  // Auto-switch text every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText(prev => (prev + 1) % texts.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);

  const experiences = [
    {
      title: "Full Stack Developer",
      company: "Logixways Solutions Pvt Ltd",
      period: "Dec 2024 – Present",
      description: "Developing full-stack web applications with modern technologies, focusing on scalable solutions and user experience."
    },
    {
      title: "Software Engineer (Vue.js)",
      company: "NexusLink Services Pvt Ltd",
      period: "Oct 2023 – Sept 2024",
      description: "Built dynamic web applications using Vue.js, implemented responsive designs, and optimized application performance."
    },
    {
      title: "Software Engineer",
      company: "Albiorix Technology",
      period: "Feb 2023 – Oct 2023",
      description: "Developed enterprise-level applications, collaborated with cross-functional teams, and mentored junior developers."
    },
    {
      title: "Vue.js Intern",
      company: "Albiorix Technology",
      period: "Aug 2022 – Jan 2023",
      description: "Learned Vue.js framework, contributed to real projects, and gained hands-on experience in modern web development."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <section id="experience" className={`py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 relative ${experienceStyles.background} transition-colors duration-500 overflow-hidden`}>
      {/* Floating Particles Background */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <FloatingParticle key={i} delay={i * 0.8} isDark={isDark} />
        ))}
      </div>
      
      {/* Cyber Grid Background */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: isDark ? 0.03 : 0.02 }}
        transition={{ duration: 2 }}
        style={{
          backgroundImage: `
            linear-gradient(${isDark ? 'rgba(249,115,22,0.3)' : 'rgba(20,184,166,0.3)'} 1px, transparent 1px),
            linear-gradient(90deg, ${isDark ? 'rgba(249,115,22,0.3)' : 'rgba(20,184,166,0.3)'} 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Futuristic Header */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mb-8 sm:mb-12 md:mb-16 relative"
        >
          {/* Cyber Status Indicator - Hidden on mobile, shown on larger screens */}
          <div className="hidden sm:block">
            <CyberStatusIndicator isDark={isDark} />
          </div>
          
          {/* Holographic Title with Text Morphing */}
          <div className="relative inline-block">
            <AnimatePresence mode="wait">
              <motion.h2
                key={currentText}
                initial={{ 
                  opacity: 0, 
                  y: 20,
                  filter: "blur(10px)",
                  textShadow: isDark ? '0 0 20px rgba(249,115,22,0)' : '0 0 20px rgba(20,184,166,0)'
                }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  filter: "blur(0px)",
                  textShadow: isDark 
                    ? '0 0 20px rgba(249,115,22,0.5), 0 0 40px rgba(249,115,22,0.3)' 
                    : '0 0 20px rgba(20,184,166,0.5), 0 0 40px rgba(20,184,166,0.3)'
                }}
                exit={{ 
                  opacity: 0, 
                  y: -20,
                  filter: "blur(10px)",
                  textShadow: isDark ? '0 0 20px rgba(249,115,22,0)' : '0 0 20px rgba(20,184,166,0)'
                }}
                transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-500 font-mono ${experienceStyles.text.title}`}
                style={{
                  background: isDark 
                    ? 'linear-gradient(135deg, #f97316 0%, #14b8a6 50%, #f97316 100%)'
                    : 'linear-gradient(135deg, #14b8a6 0%, #f97316 50%, #14b8a6 100%)',
                  backgroundSize: '200% 200%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {texts[currentText]}
              </motion.h2>
            </AnimatePresence>
            
            {/* Holographic Scanlines */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ 
                backgroundPosition: ['0% 0%', '100% 100%']
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "linear"
              }}
              style={{
                background: `repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent 2px,
                  ${isDark ? 'rgba(249,115,22,0.1)' : 'rgba(20,184,166,0.1)'} 2px,
                  ${isDark ? 'rgba(249,115,22,0.1)' : 'rgba(20,184,166,0.1)'} 4px
                )`,
                backgroundSize: '10px 100%'
              }}
            />
          </div>

          {/* Animated Underline */}
          <motion.div
            className={`w-16 sm:w-20 h-1 mx-auto rounded-full relative overflow-hidden`}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: 'circOut' }}
            style={{ 
              background: isDark 
                ? 'linear-gradient(90deg, #f97316, #14b8a6)' 
                : 'linear-gradient(90deg, #14b8a6, #f97316)',
              originX: 0.5 
            }}
          >
            {/* Flowing light effect */}
            <motion.div
              className="absolute inset-0 w-full h-full"
              animate={{
                x: ['-100%', '100%']
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                background: `linear-gradient(90deg, 
                  transparent 0%, 
                  ${isDark ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)'} 50%, 
                  transparent 100%
                )`,
                filter: 'blur(2px)'
              }}
          />
          </motion.div>

          {/* Matrix-style subtitle */}
          <motion.div
            initial={{ opacity: 0, filter: "blur(5px)" }}
            whileInView={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-4"
          >
            <span className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-600'} tracking-[0.3em]`}>
              &gt; PORTFOLIO.exe --display-career
            </span>
          </motion.div>

          {/* Mobile Status Indicator */}
          <motion.div 
            className="flex sm:hidden items-center justify-center gap-1.5 mt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
          >
            {/* Pulsing Status Dot */}
            <motion.div
              className={`w-2 h-2 rounded-full ${isDark ? 'bg-green-400' : 'bg-green-500'}`}
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ 
                boxShadow: isDark 
                  ? '0 0 8px rgba(74, 222, 128, 0.6)' 
                  : '0 0 8px rgba(34, 197, 94, 0.6)'
              }}
            />
            
            {/* Status Text */}
            <motion.span 
              className={`text-[10px] font-mono ${isDark ? 'text-green-400' : 'text-green-600'} tracking-wider`}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              AVAILABLE
            </motion.span>
          </motion.div>
        </motion.div>

        {/* Desktop/Tablet Experience Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="hidden sm:grid gap-8 sm:gap-10 md:gap-12 grid-cols-1 md:grid-cols-2 justify-center items-stretch max-w-4xl mx-auto px-4 sm:px-6 md:px-8"
        >
          {experiences.map((exp, index) => (
            <ExperienceCard
              key={index}
              exp={exp}
              isDark={isDark}
              index={index}
            />
          ))}
        </motion.div>

        {/* Mobile Experience Flow - Creative Design */}
        <div className="block sm:hidden px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="relative max-w-sm mx-auto"
          >
            {/* Flowing Background Stream */}
            <motion.div
              className="absolute inset-0 -z-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <svg 
                viewBox="0 0 300 800" 
                className={`w-full h-full ${isDark ? 'opacity-60' : 'opacity-30'}`}
                style={{ minHeight: `${experiences.length * 180}px` }}
              >
                {/* Main flowing path */}
                <motion.path
                  d="M150 0 Q50 100 150 200 Q250 300 150 400 Q50 500 150 600 Q250 700 150 800"
                  fill="none"
                  stroke={isDark ? 'url(#gradientDark)' : 'url(#gradientLight)'}
                  strokeWidth={isDark ? "50" : "45"}
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                />
                
                {/* Animated flowing energy - Dark theme specific */}
                {isDark && (
                  <motion.path
                    d="M150 0 Q50 100 150 200 Q250 300 150 400 Q50 500 150 600 Q250 700 150 800"
                    fill="none"
                    stroke="url(#flowingEnergyDark)"
                    strokeWidth="15"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, pathOffset: 0 }}
                    animate={{ 
                      pathLength: [0, 0.4, 0],
                      pathOffset: [0, 1, 1]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 0.5
                    }}
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(249,115,22,0.6)) drop-shadow(0 0 15px rgba(20,184,166,0.4))'
                    }}
                  />
                )}
                
                {/* Animated flowing energy - Light theme specific */}
                {!isDark && (
                  <motion.path
                    d="M150 0 Q50 100 150 200 Q250 300 150 400 Q50 500 150 600 Q250 700 150 800"
                    fill="none"
                    stroke="url(#flowingEnergyLight)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, pathOffset: 0 }}
                    animate={{ 
                      pathLength: [0, 0.25, 0],
                      pathOffset: [0, 1, 1]
                    }}
                    transition={{ 
                      duration: 3.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 1.5
                    }}
                  />
                )}

                <defs>
                  <linearGradient id="gradientDark" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" stopOpacity="0.7" />
                    <stop offset="50%" stopColor="#14b8a6" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#f97316" stopOpacity="0.7" />
                  </linearGradient>
                  <linearGradient id="gradientLight" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.4" />
                    <stop offset="50%" stopColor="#f97316" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.4" />
                  </linearGradient>
                  
                  {/* Enhanced flowing energy gradients */}
                  <linearGradient id="flowingEnergyDark" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
                    <stop offset="20%" stopColor="#f97316" stopOpacity="1" />
                    <stop offset="40%" stopColor="#14b8a6" stopOpacity="1" />
                    <stop offset="60%" stopColor="#f97316" stopOpacity="1" />
                    <stop offset="80%" stopColor="#14b8a6" stopOpacity="1" />
                    <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                  </linearGradient>
                  
                  <linearGradient id="flowingEnergyLight" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
                    <stop offset="30%" stopColor="#14b8a6" stopOpacity="0.7" />
                    <stop offset="50%" stopColor="#f97316" stopOpacity="0.9" />
                    <stop offset="70%" stopColor="#14b8a6" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>

            {/* Experience Flow Items */}
            <div className="relative z-10 space-y-6">
              {experiences.map((exp, index) => {
                const isLeft = index % 2 === 0;
                const yOffset = index * 180;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ 
                      opacity: 0, 
                      x: isLeft ? -60 : 60,
                      y: 20 
                    }}
                    whileInView={{ 
                      opacity: 1, 
                      x: 0,
                      y: 0 
                    }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ 
                      duration: 0.8, 
                      delay: index * 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    className={`
                      relative
                      ${isLeft ? 'text-left pr-8' : 'text-right pl-8'}
                    `}
                    style={{ 
                      marginTop: index === 0 ? '0px' : '40px'
                    }}
                  >
                    {/* Floating Orb */}
                    <motion.div
                      className={`
                        absolute top-6 w-4 h-4 rounded-full
                        ${isLeft ? 'right-2' : 'left-2'}
                        ${index === 0 
                          ? (isDark ? 'bg-green-400' : 'bg-orange-500')
                          : (isDark ? 'bg-orange-400' : 'bg-teal-400')
                        }
                      `}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.3, duration: 0.5 }}
                      animate={{ 
                        y: [0, -8, 0],
                        boxShadow: [
                          `0 0 0 0 ${index === 0 
                            ? (isDark ? 'rgba(74, 222, 128, 0.4)' : 'rgba(249,115,22,0.4)')
                            : (isDark ? 'rgba(249,115,22,0.4)' : 'rgba(20,184,166,0.4)')
                          }`,
                          `0 0 0 16px ${index === 0 
                            ? (isDark ? 'rgba(74, 222, 128, 0)' : 'rgba(249,115,22,0)')
                            : (isDark ? 'rgba(249,115,22,0)' : 'rgba(20,184,166,0)')
                          }`
                        ],
                        transition: { 
                          y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                          boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                        }
                      }}
                    />

                    {/* Content Bubble */}
                    <motion.div
                      className={`
                        relative overflow-hidden
                        ${isDark ? 'text-white' : 'text-gray-900'}
                      `}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {/* Organic Background Shape */}
                      <motion.div
                        className={`
                          absolute inset-0 
                          ${isDark 
                            ? 'bg-gradient-to-br from-black/60 to-black/40' 
                            : 'bg-gradient-to-br from-white/85 to-gray-100/70'
                          }
                          backdrop-blur-sm border
                          ${isDark ? 'border-gray-700/50' : 'border-gray-500/30'}
                        `}
                        style={{
                          borderRadius: isLeft 
                            ? '30px 80px 30px 80px' 
                            : '80px 30px 80px 30px',
                          transform: `rotate(${index * 2 - 4}deg)`
                        }}
                        initial={{ scale: 0.8, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2 + index * 0.3, duration: 0.6 }}
                      />

                      {/* Content */}
                      <div className="relative z-10 p-6">
                        {/* Period - Minimalist */}
                        <motion.div
                          className="mb-3"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + index * 0.3 }}
                        >
                          <span className={`
                            text-[9px] font-mono tracking-widest uppercase
                            ${isDark ? 'text-orange-300' : 'text-teal-700'}
                            ${isDark ? 'opacity-80' : 'opacity-90'}
                            px-2 py-1 rounded-full
                            ${isDark ? 'bg-orange-500/10' : 'bg-teal-500/15'}
                          `}>
                            {exp.period}
                          </span>
                        </motion.div>

                        {/* Title - Bold Typography */}
                        <motion.h3 
                          className="text-base font-bold mb-1 font-mono leading-tight"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 + index * 0.3 }}
                          whileHover={{
                            scale: 1.02,
                            transition: { duration: 0.2 }
                          }}
                        >
                          {exp.title}
                        </motion.h3>

                        {/* Company - Accent */}
                        <motion.p 
                          className={`
                            text-sm font-medium mb-3 font-mono
                            ${isDark ? 'text-orange-400' : 'text-teal-600'}
                          `}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7 + index * 0.3 }}
                        >
                          {exp.company}
                        </motion.p>

                        {/* Description - Clean */}
                        <motion.p 
                          className={`
                            text-xs leading-relaxed font-mono opacity-90
                            ${isDark ? 'text-gray-300' : 'text-gray-700'}
                          `}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 + index * 0.3 }}
                        >
                          {exp.description}
                        </motion.p>

                        {/* Experience Indicators - Clean & Minimal */}
                        <motion.div 
                          className={`mt-4 ${isLeft ? 'text-left ml-4' : 'text-right mr-4'}`}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 0.9 + index * 0.3 }}
                        >
                          {/* Seniority & Role */}
                          <div className={`flex items-center gap-2 ${isLeft ? 'justify-start' : 'justify-end'}`}>
                            {/* Seniority Level Dots */}
                            <div className={`flex items-center gap-0.5 ${!isLeft ? 'flex-row-reverse' : ''}`}>
                              {[...Array(4)].map((_, i) => {
                                const getSeniorityLevel = () => {
                                  if (index === 0) return 4; // Full Stack Developer (highest)
                                  if (index === 1) return 3; // Software Engineer (Vue.js)
                                  if (index === 2) return 2; // Software Engineer
                                  return 1; // Intern (entry level)
                                };
                                
                                return (
                                  <motion.div
                                    key={i}
                                    className={`w-1 h-1 rounded-full ${
                                      i < getSeniorityLevel() 
                                        ? (isDark ? 'bg-orange-400' : 'bg-teal-500') 
                                        : (isDark ? 'bg-gray-700' : 'bg-gray-300')
                                    }`}
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ delay: 1 + index * 0.3 + i * 0.1, duration: 0.3 }}
                                  />
                                );
                              })}
                            </div>
                            
                            {/* Role Type Indicator */}
                            <span className={`text-[8px] font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'} ${!isLeft ? 'order-first' : ''}`}>
                              {index === 0 ? 'SENIOR' : index === 1 ? 'MID' : index === 2 ? 'MID' : 'JUNIOR'}
                            </span>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>


    </section>
  );
};

export default Experience;
